import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  getLastIndexOfArray,
  Guid,
  isNil,
  isNotNil,
  Nillable,
  valueOrDefault,
} from '@web/utils';
import {
  WarehouseRestockFormData,
  WarehouseRestockFormGroup,
  WarehouseRestockPersonFormData,
  WarehouseRestockPersonFormGroup,
  WarehouseRestockProductFormData,
  WarehouseRestockProductFormGroup,
} from '../../models/warehouse-restock-form-data';
import {
  ArrayValidator,
  EmailValidator,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengSelectDirective,
  PrimengTextareaDirective,
  SelectItem,
} from '@web/shared/ui';
import { ButtonModule } from 'primeng/button';
import { WarehouseRestockType } from '../../enums/warehouse-restock-type';
import { InputTextModule } from 'primeng/inputtext';
import { Observable } from 'rxjs';
import { WarehouseRestockTypeProviderService } from '../../providers/warehouse-restock-type-provider.service';
import { Textarea } from 'primeng/textarea';
import { Accordion } from 'primeng/accordion';
import { DatePicker } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import { Fieldset } from 'primeng/fieldset';
import { WarehouseFilesUploadComponent } from '../../containers/warehouse-files-upload/warehouse-files-upload.component';
import { WarehouseReceiptFormData } from '../../models/warehouse-receipt-form-data';
import { WarehouseRestockFormProductPanelComponent } from '../warehouse-restock-form-product-panel/warehouse-restock-form-product-panel.component';

@Component({
  selector: 'app-warehouse-restock-form',
  imports: [
    Accordion,
    ButtonModule,
    CommonModule,
    DatePicker,
    Fieldset,
    FormControlComponent,
    InputTextModule,
    PrimengButtonDirective,
    PrimengSelectDirective,
    PrimengTextareaDirective,
    ReactiveFormsModule,
    Select,
    Textarea,
    WarehouseFilesUploadComponent,
    WarehouseRestockFormProductPanelComponent,
  ],
  templateUrl: './warehouse-restock-form.component.html',
  styleUrl: './warehouse-restock-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseRestockFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly restockTypeProvider = inject(WarehouseRestockTypeProviderService);

  public readonly data = input<Nillable<WarehouseRestockFormData>>();
  public readonly fileUploadVisibility = input(false);
  public readonly submitSuccessEvent = output<WarehouseRestockFormData>();

  protected readonly accordion = viewChild(Accordion);
  protected readonly form = this.buildForm();
  protected readonly typeSelectItems$ = this.typeSelectItems();
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly isNotNil = isNotNil;

  protected activeProductIndex = 0;

  public ngOnInit(): void {
    this.fillByData();
  }

  public handleSubmitClick(): void {
    const data = this.form.getRawValue();

    this.submitSuccessEvent.emit(
      new WarehouseRestockFormData({
        comment: data.comment,
        person: isNil(data.person)
          ? null
          : new WarehouseRestockPersonFormData({
              dignity: data.person?.dignity,
              phone: data.person?.phone,
              email: data.person?.email,
            }),
        restockedAt: data.restockedAt,
        shippingNumber: data.shippingNumber,
        type: data.type,
        products: data.products.map(
          (product) =>
            new WarehouseRestockProductFormData({
              productId: product.id!,
              quantity: product.quantity!,
            }),
        ),
        fileIds: data.fileIds,
      }),
    );
  }

  public getProductFormGroups(): WarehouseRestockProductFormGroup[] {
    return this.form.controls.products.controls;
  }

  public handleProductRemoveClick(index: number): void {
    this.form.controls.products.removeAt(index);
    this.activeProductIndex = this.form.controls.products.length - 1;
  }

  public handleProductAddClick(): void {
    this.form.controls.products.push(this.buildProductFormGroup());
    this.activeProductIndex = this.form.controls.products.length - 1;
  }

  private buildForm(): WarehouseRestockFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      restockedAt: this.builder.nonNullable.control(new Date(), [Validators.required]),
      comment: this.builder.control<Nillable<string>>(null, [Validators.maxLength(WarehouseRestockFormData.MaxCommentLength)]),
      shippingNumber: this.builder.control<Nillable<string>>(null, Validators.maxLength(WarehouseRestockFormData.MaxShippingNumberLength)),
      type: this.builder.nonNullable.control<WarehouseRestockType>(WarehouseRestockType.Return, [Validators.required]),
      person: this.buildPersonFormGroup(),
      products: this.builder.array<WarehouseRestockProductFormGroup>([], [ArrayValidator.min(WarehouseReceiptFormData.MinProductCount)]),
      fileIds: this.builder.control<Nillable<Guid[]>>(null)
    })
  }

  private buildPersonFormGroup(): WarehouseRestockPersonFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      dignity: this.builder.control<Nillable<string>>(null),
      email: this.builder.control<Nillable<string>>(null, [EmailValidator.email]),
      phone: this.builder.control<Nillable<string>>(null),
    })
  }

  private buildProductFormGroup(
    data?: WarehouseRestockProductFormData,
  ): WarehouseRestockProductFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      id: this.builder.control<Nillable<Guid>>(data?.productId),
      quantity: this.builder.control<Nillable<number>>(data?.quantity),
    })
  }

  private fillByData(): void {
    const data = this.data();

    if (isNil(data)) {
      return;
    }

    const products = valueOrDefault(data.products, []);

    products.forEach((product) =>
      this.form.controls.products.push(this.buildProductFormGroup(product)),
    );

    this.activeProductIndex = getLastIndexOfArray(products);

    this.form.patchValue(
      {
        comment: data?.comment,
        person: {
          dignity: data?.person?.dignity,
          phone: data?.person?.phone,
          email: data?.person?.email,
        },
        restockedAt: data?.restockedAt,
        shippingNumber: data?.shippingNumber,
        fileIds: data?.fileIds,
        type: data?.type,
      },
      { emitEvent: false },
    );
  }

  private typeSelectItems(): Observable<SelectItem<WarehouseRestockType>[]> {
    return this.restockTypeProvider.selectItems();
  }
}
